import Service, { service } from '@ember/service';
import type Store from '@ember-data/store';
import type AlertingService from '@mylighthouse/prism-ember/services/alerting';
import { task } from 'ember-concurrency';
import type ErrorTrackingService from 'frontend-utils/services/error-tracking';
import type { ChannelUrlsConfig, MarketingRecommendation } from 'lumen/models';
import type AccessService from 'lumen/services/access';
import {
  getTranslationForNoPriceRecommendation,
  getTranslationForPriceDecrease,
} from 'lumen/utils/components/general/visibility-boost/reason-translations';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export default class MarketingRecommendationService extends Service {
  @service declare access: AccessService;
  @service declare alerting: AlertingService;
  @service declare errorTracking: ErrorTrackingService;
  @service declare store: Store;

  /** Inspired by clearCachedLosRestrictionRates, in apps/lumen/app/services/rates.ts */
  cachedReasonTranslationPerDate: Record<string, string> = {};

  fetchChannelUrlsConfigTask = task(
    { restartable: true },
    async (hotelId: string) => {
      const requestParams = {
        namespace: APIGATEWAY,
        typeKey: 'matching/marketing_recommendation_urls',
        subscription_id: hotelId,
      };

      try {
        // The result of the query is now returned from the task
        return await this.store.queryRecord<ChannelUrlsConfig>(
          'channel-urls-config',
          requestParams,
        );
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  /**
   * Task that fetches marketing recommendations for a specific hotel within a given date range.
   *
   * On a successful fetch, the recommendations are loaded into the Ember Data store.
   * If the API request fails, it displays an error alert to the user, logs the exception,
   * and throws a `SilentTaskError` to prevent unhandled promise rejections.
   *
   * @param hotelId - The ID of the hotel for which to fetch recommendations.
   * @param rangeStart - The start date of the date range (e.g., 'YYYY-MM-DD').
   * @param rangeEnd - The end date of the date range (e.g., 'YYYY-MM-DD').
   */
  fetchRecommendationsTask = task(
    { restartable: true },
    async (
      hotelId: string,
      rangeStart: string,
      rangeEnd: string,
    ): Promise<void> => {
      if (!this.access.hasVisibilityBoostViewAccess) {
        return;
      }

      const requestParams = {
        namespace: APIGATEWAY,
        typeKey: 'marketing/recommendation',
        from_date_range_start: rangeStart,
        from_date_range_end: rangeEnd,
        hotel_id: hotelId,
      };

      try {
        await this.store.query<MarketingRecommendation>(
          'marketing/recommendation',
          requestParams,
        );
      } catch (error) {
        this.errorTracking.captureException(error);
        this.errorTracking.captureMessage(
          'Marketing recommendation data request failed',
        );

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  /**
   * Cleanup data and resources from this service.
   *
   * TODO: Discuss in the future if this is needed.
   *       Maybe the recommendations will always be the same, regardless the main
   *       route, therefore, the cache could persist during the whole app session.
   *       In the first implementation this function is called in pricing-manager
   *       route's deactivate function as a standard good practice.
   */

  cleanUp(): void {
    this.cachedReasonTranslationPerDate = {};
    void this.fetchRecommendationsTask.cancelAll();
    void this.fetchChannelUrlsConfigTask.cancelAll();
    this.store.unloadAll('marketing/recommendation');
  }

  /**
   * Returns translation cached keys for no-price recommendation.
   *
   * Cache is store locally in the service. Pay attention in case the cache is
   * being removed in case the route is destroyed.
   *
   * @param date
   */
  getCachedTranslationForNoPriceRecommendation(date: string): string {
    const cached = this.cachedReasonTranslationPerDate[date];

    if (cached) {
      return cached;
    }

    const key = getTranslationForNoPriceRecommendation();
    this.cachedReasonTranslationPerDate[date] = key;

    return key;
  }

  /**
   * Returns translation cached keys for price-decrease recommendation.
   *
   * Cache is store locally in the service. Pay attention in case the cache is
   * being removed in case the route is destroyed.
   *
   * @param date
   */
  getCachedTranslationForPriceDecrease(date: string): string {
    const cached = this.cachedReasonTranslationPerDate[date];

    if (cached) {
      return cached;
    }

    const key = getTranslationForPriceDecrease();
    this.cachedReasonTranslationPerDate[date] = key;

    return key;
  }
}

declare module '@ember/service' {
  interface Registry {
    'marketing/recommendation': MarketingRecommendationService;
  }
}
